import { getTimeZoneId } from '@csp/csp-common-date-util';
import { Maybe } from '@csp/csp-common-model';
import { KitTypeAndDescriptions } from '@csp/csp-common-hbs-model';
import { BioSampleContainerQueryService } from '@csp/csp-common-hbs';
import { UnbookedVisitMapper } from '../model/UnbookedVisitMapper';
import { UnbookedVisits } from '../model/UnbookedVisits';
import { VisitsGeneratorService } from '../schedule/service/VisitsGeneratorService';
import { GeneratedVisit } from '../schedule/model/GeneratedVisit';
import { ScheduleVisitsContent } from '../model/ScheduleVisitsContent';
import { UnbookedPatientVisitIncludeFields } from './UnbookedPatientVisitIncludeFields';
import { UnbookedVisitsIn } from './UnbookedVisitsIn';

const queryKitDescByTypesIfEnabled = async (
  generatedVisits: GeneratedVisit[],
  includeFields: UnbookedPatientVisitIncludeFields,
): Promise<Maybe<KitTypeAndDescriptions>> => {
  const hbsEnabledVisitRequests = generatedVisits?.filter(req => req.request.hbs.enabled);
  if (includeFields?.supportedKits && hbsEnabledVisitRequests) {
    const allKitTypes = generatedVisits.flatMap(visit => visit.request.hbs.kitTypes) || [];
    return await BioSampleContainerQueryService.queryCachedKitDescriptionByTypes(allKitTypes);
  } else {
    return undefined;
  }
};

const getUnbookedVisits = async ({
  patient,
  study,
  patientVisitTexts,
  bookedVisits,
  timezoneId = getTimeZoneId(),
  includeFields = {},
  scheduleVisitsTexts,
}: UnbookedVisitsIn): Promise<UnbookedVisits> => {
  const visitContent = ScheduleVisitsContent.fromCmsScheduleVisits(scheduleVisitsTexts);
  const generatedVisits = await VisitsGeneratorService.generateScheduledVisits(
    patient,
    study,
    timezoneId,
    bookedVisits,
  );

  const kitTypesAndDescsForAllVisits = await queryKitDescByTypesIfEnabled(
    generatedVisits.unbookedVisits,
    includeFields,
  );

  const unbookedVisits = generatedVisits.unbookedVisits.map(generatedVisit =>
    UnbookedVisitMapper.from(generatedVisit, patient, patientVisitTexts, visitContent, kitTypesAndDescsForAllVisits),
  );

  return UnbookedVisits.from(unbookedVisits, generatedVisits.hasScheduledVisits);
};

export const UnbookedVisitService = {
  getUnbookedVisits,
};
